import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import FilingCopilot from './FilingCopilot';

const API_BASE = '';

const US_STATES = [
  'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware','Florida','Georgia',
  'Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky','Louisiana','Maine','Maryland',
  'Massachusetts','Michigan','Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire','New Jersey',
  'New Mexico','New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina',
  'South Dakota','Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia','Wisconsin','Wyoming','District of Columbia'
];

const SERVICES = [
  { id: 'llc', icon: '🏢', title: 'LLC Formation', description: 'Form a Limited Liability Company with state-specific guidance, required documents, and filing instructions.', color: '#6366f1' },
  { id: 'dba', icon: '📋', title: 'DBA Registration', description: 'Register a "Doing Business As" name with your state or county, including publication requirements.', color: '#0ea5e9' },
  { id: 'trademark', icon: '™️', title: 'Trademark Application', description: 'Search existing marks, classify goods/services, and prepare your USPTO trademark application.', color: '#8b5cf6' },
];

function StepProgress({ steps }) {
  if (!steps?.length) return null;
  const completed = steps.filter(s => s.status === 'completed').length;
  const pct = Math.round((completed / steps.length) * 100);
  return (
    <div style={{ background: '#f8f9fa', border: '1px solid #e0e0e0', borderRadius: 12, padding: 20, marginBottom: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontWeight: 600 }}>Processing</span>
        <span style={{ fontWeight: 600, color: '#1565c0' }}>{pct}%</span>
      </div>
      <div style={{ height: 8, background: '#e0e0e0', borderRadius: 4, overflow: 'hidden', marginBottom: 16 }}>
        <div style={{ height: '100%', width: `${pct}%`, background: '#1565c0', borderRadius: 4, transition: 'width 0.5s ease' }} />
      </div>
      {steps.map((step, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <span style={{ width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 'bold', color: 'white', background: step.status === 'completed' ? '#2e7d32' : step.status === 'active' ? '#1565c0' : '#9e9e9e', animation: step.status === 'active' ? 'pulse 1.5s infinite' : 'none' }}>
            {step.status === 'completed' ? '✓' : step.status === 'active' ? '●' : '○'}
          </span>
          <span style={{ color: step.status === 'completed' ? '#2e7d32' : step.status === 'active' ? '#1565c0' : '#9e9e9e', fontWeight: step.status === 'active' ? 600 : 400, fontSize: 14 }}>{step.label}</span>
        </div>
      ))}
    </div>
  );
}

function CostCard({ label, value, note }) {
  return (
    <div style={{ padding: 16, borderRadius: 8, background: '#f8fafc', textAlign: 'center', border: '1px solid #e2e8f0' }}>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: '#1e293b' }}>{typeof value === 'number' ? `$${value.toLocaleString()}` : value || '—'}</div>
      {note && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{note}</div>}
    </div>
  );
}

export default function LegalServices() {
  const { user } = useAuth();
  const params = useParams();
  const navigate = useNavigate();
  const [activeService, setActiveService] = useState(null);
  const [currentRequest, setCurrentRequest] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({});
  const [showCopilot, setShowCopilot] = useState(false);
  const [strategyId, setStrategyId] = useState(null);

  // Pre-fill from strategy (via ?prefill= query param)
  useEffect(() => {
    const search = new URLSearchParams(window.location.search);
    const prefill = search.get('prefill');
    if (prefill) {
      const p = new URLSearchParams(prefill);
      const prefillData = {};
      if (p.get('companyName')) prefillData.companyName = p.get('companyName');
      if (p.get('state')) prefillData.state = p.get('state');
      if (p.get('managementType')) prefillData.managementType = p.get('managementType');
      if (p.get('businessPurpose')) prefillData.businessPurpose = p.get('businessPurpose');
      if (p.get('strategyId')) setStrategyId(p.get('strategyId'));
      if (Object.keys(prefillData).length > 0) {
        setForm(prefillData);
        setActiveService('llc');
      }
    }
  }, []);

  useEffect(() => { if (user) fetchRequests(); }, [user]);

  // Deep link: load request by UUID from URL
  useEffect(() => {
    if (!params.uuid || !user) return;
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/legal-services/${params.uuid}`, { credentials: 'include' });
        const d = await r.json();
        if (d.success && d.request) {
          setCurrentRequest(d.request);
          setActiveService(d.request.type);
          // Auto-open copilot if /copilot path
          if (window.location.pathname.endsWith('/copilot')) {
            setShowCopilot(true);
          }
        }
      } catch (e) { console.error('Failed to load request:', e); }
    })();
  }, [params.uuid, user]);

  const fetchRequests = async () => {
    try { const r = await fetch(`${API_BASE}/api/legal-services`, { credentials: 'include' }); const d = await r.json(); if (d.success) setRequests(d.requests); } catch {}
  };

  const pollResult = useCallback((uuid) => {
    const poll = setInterval(async () => {
      try {
        const r = await fetch(`${API_BASE}/api/legal-services/${uuid}`, { credentials: 'include' });
        const d = await r.json();
        if (d.success) {
          setCurrentRequest(d.request);
          if (d.request.status === 'completed' || d.request.status === 'failed') { clearInterval(poll); fetchRequests(); }
        }
      } catch { clearInterval(poll); }
    }, 2000);
  }, []);

  const handleSubmit = async (type) => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/api/legal-services/${type}`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...form, strategyId }),
      });
      const d = await r.json();
      if (d.success) { setCurrentRequest({ uuid: d.uuid, status: 'processing', steps: [] }); pollResult(d.uuid); navigate('/legal-services/' + d.uuid, { replace: true }); }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const reset = () => { setActiveService(null); setCurrentRequest(null); setForm({}); setShowCopilot(false); navigate('/legal-services', { replace: true }); };

  const loadRequest = (req) => { setCurrentRequest(req); setActiveService(req.type); navigate('/legal-services/' + req.uuid, { replace: true }); };

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  // Results rendering helpers
  const renderLLCResults = (results) => {
    const req = results.requirements || {};
    const checklist = results.checklist || {};
    const docs = results.documents || {};
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Costs Overview */}
        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24 }}>
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Filing Costs — {req.state}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
            <CostCard label="State Filing Fee" value={req.stateFee} />
            <CostCard label="Expedited Fee" value={req.expeditedFee} note="Optional" />
            <CostCard label="Annual Report" value={req.annualReportFee} note="Yearly" />
            <CostCard label="Registered Agent" value={req.registeredAgentCost} note="Yearly" />
            {req.publicationRequired && <CostCard label="Publication" value={req.publicationCost} note="One-time" />}
          </div>
          {checklist.totalEstimatedCost && (
            <div style={{ marginTop: 16, padding: 14, borderRadius: 8, background: '#eff6ff', border: '1px solid #bfdbfe', textAlign: 'center' }}>
              <span style={{ fontSize: 14, color: '#1e40af' }}>Total Estimated Cost: </span>
              <span style={{ fontSize: 20, fontWeight: 700, color: '#1e40af' }}>
                ${checklist.totalEstimatedCost.minimum?.toLocaleString()} – ${checklist.totalEstimatedCost.maximum?.toLocaleString()}
              </span>
            </div>
          )}
        </div>
        {/* Requirements */}
        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24 }}>
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>State Requirements</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div style={{ padding: 10, borderRadius: 6, background: '#f8fafc', fontSize: 14 }}>Processing: <strong>{req.processingTime}</strong></div>
            <div style={{ padding: 10, borderRadius: 6, background: '#f8fafc', fontSize: 14 }}>Expedited: <strong>{req.expeditedTime}</strong></div>
            <div style={{ padding: 10, borderRadius: 6, background: '#f8fafc', fontSize: 14 }}>Online Filing: <strong>{req.onlineFilingAvailable ? 'Yes' : 'No'}</strong></div>
            <div style={{ padding: 10, borderRadius: 6, background: '#f8fafc', fontSize: 14 }}>Operating Agreement: <strong>{req.operatingAgreementRequired ? 'Required' : 'Recommended'}</strong></div>
          </div>
          {req.filingUrl && (
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 12, flexWrap: 'wrap' }}>
              <button onClick={() => { setShowCopilot(true); navigate('/legal-services/' + currentRequest.uuid + '/copilot', { replace: true }); }} style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: '#6366f1', color: 'white', fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 16 }}>🧭</span> Launch Filing Copilot
              </button>
              <a href={req.filingUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#6366f1', fontWeight: 500, fontSize: 13 }}>or open portal directly →</a>
            </div>
          )}
        </div>
        {/* Filing Checklist */}
        {checklist.filingSteps?.length > 0 && (
          <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24 }}>
            <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Filing Checklist</h3>
            {['preFilingSteps', 'filingSteps', 'postFilingSteps'].map(section => {
              const items = checklist[section];
              if (!items?.length) return null;
              const label = section === 'preFilingSteps' ? 'Before Filing' : section === 'filingSteps' ? 'Filing Steps' : 'After Filing';
              return (
                <div key={section} style={{ marginBottom: 16 }}>
                  <h4 style={{ fontSize: 14, fontWeight: 600, color: '#6366f1', marginBottom: 8 }}>{label}</h4>
                  {items.map((item, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 12px', borderRadius: 8, background: '#f8fafc', marginBottom: 6 }}>
                      <span style={{ width: 24, height: 24, borderRadius: '50%', background: '#6366f1', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>{i + 1}</span>
                      <div>
                        <div style={{ fontWeight: 500, fontSize: 14 }}>{item.step || item.action}</div>
                        {item.description && <div style={{ fontSize: 13, color: '#64748b' }}>{item.description}</div>}
                        {item.deadline && <div style={{ fontSize: 12, color: '#d97706' }}>Deadline: {item.deadline}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}
        {/* Operating Agreement */}
        {docs.operatingAgreement?.content && (
          <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24 }}>
            <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Operating Agreement Template</h3>
            <pre style={{ background: '#f8fafc', padding: 16, borderRadius: 8, fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap', maxHeight: 400, overflow: 'auto', border: '1px solid #e2e8f0' }}>{docs.operatingAgreement.content}</pre>
          </div>
        )}
      </div>
    );
  };

  const renderDBAResults = (results) => {
    const req = results.requirements || {};
    const docs = results.documents || {};
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24 }}>
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>DBA Requirements — {req.state}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 16 }}>
            <CostCard label="Filing Fee" value={req.fee} />
            <CostCard label="Renewal Fee" value={req.renewalFee} note={req.renewalPeriod} />
            {req.publicationRequired && <CostCard label="Publication" value={req.publicationCost} note={req.publicationDuration} />}
          </div>
          {docs.totalCost && (
            <div style={{ padding: 14, borderRadius: 8, background: '#eff6ff', border: '1px solid #bfdbfe', textAlign: 'center' }}>
              <span style={{ fontSize: 14, color: '#1e40af' }}>Total Cost: </span>
              <span style={{ fontSize: 20, fontWeight: 700, color: '#1e40af' }}>${docs.totalCost.minimum} – ${docs.totalCost.maximum}</span>
            </div>
          )}
        </div>
        {docs.filingSteps?.length > 0 && (
          <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24 }}>
            <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Filing Steps</h3>
            {docs.filingSteps.map((step, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 12px', borderRadius: 8, background: '#f8fafc', marginBottom: 6 }}>
                <span style={{ width: 24, height: 24, borderRadius: '50%', background: '#0ea5e9', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>{step.step}</span>
                <div>
                  <div style={{ fontWeight: 500, fontSize: 14 }}>{step.action}</div>
                  <div style={{ fontSize: 13, color: '#64748b' }}>{step.details}</div>
                  {step.cost > 0 && <div style={{ fontSize: 12, color: '#0ea5e9', fontWeight: 500 }}>${step.cost}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderTrademarkResults = (results) => {
    const search = results.search || {};
    const classification = results.classification || {};
    const app = results.application || {};
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Search Results */}
        <div style={{ background: search.overallRisk === 'high' ? '#fef2f2' : search.overallRisk === 'medium' ? '#fef3c7' : '#ecfdf5', borderRadius: 12, padding: 20, border: `2px solid ${search.overallRisk === 'high' ? '#fecaca' : search.overallRisk === 'medium' ? '#fde68a' : '#a7f3d0'}` }}>
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Trademark Search — Risk: <span style={{ textTransform: 'capitalize' }}>{search.overallRisk || 'Unknown'}</span></h3>
          <p style={{ fontSize: 14, color: '#475569' }}>{search.recommendation || search.searchSummary}</p>
        </div>
        {search.potentialConflicts?.length > 0 && (
          <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24 }}>
            <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Potential Conflicts</h3>
            {search.potentialConflicts.map((c, i) => (
              <div key={i} style={{ padding: 14, borderRadius: 8, background: '#f8fafc', borderLeft: `4px solid ${c.riskLevel === 'high' ? '#dc2626' : c.riskLevel === 'medium' ? '#d97706' : '#059669'}`, marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: 600 }}>{c.mark}</span>
                  <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', background: c.riskLevel === 'high' ? '#fef2f2' : c.riskLevel === 'medium' ? '#fef3c7' : '#ecfdf5', color: c.riskLevel === 'high' ? '#dc2626' : c.riskLevel === 'medium' ? '#d97706' : '#059669' }}>{c.riskLevel}</span>
                </div>
                <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>{c.owner} • {c.status} • {c.goodsServices}</div>
              </div>
            ))}
          </div>
        )}
        {/* Classification & Costs */}
        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24 }}>
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Classification & Filing Costs</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 16 }}>
            <CostCard label="Filing Fees" value={app.applicationOverview?.totalCost?.filingFees || classification.totalFilingFee} />
            <CostCard label="Attorney Fees" value={app.applicationOverview?.totalCost?.attorneyFees} note="Estimated" />
            <CostCard label="Total Minimum" value={app.applicationOverview?.totalCost?.totalMinimum} />
            <CostCard label="Total Maximum" value={app.applicationOverview?.totalCost?.totalMaximum} />
          </div>
          {classification.suggestedClasses?.map((cls, i) => (
            <div key={i} style={{ padding: 10, borderRadius: 6, background: '#f8fafc', marginBottom: 6, fontSize: 14, display: 'flex', justifyContent: 'space-between' }}>
              <span>Class {cls.classNumber}: {cls.className}</span>
              <span style={{ color: '#6366f1', fontWeight: 600 }}>${cls.filingFee}</span>
            </div>
          ))}
        </div>
        {/* Timeline */}
        {app.postFilingTimeline?.length > 0 && (
          <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24 }}>
            <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Application Timeline</h3>
            {app.postFilingTimeline.map((stage, i) => (
              <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                <div style={{ width: 3, background: '#6366f1', borderRadius: 2, flexShrink: 0 }} />
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{stage.stage}</div>
                  <div style={{ fontSize: 13, color: '#6366f1' }}>{stage.timeframe}</div>
                  <div style={{ fontSize: 13, color: '#64748b' }}>{stage.description}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Form renderers
  const helpStyle = { fontSize: 12, color: '#6b7280', marginTop: 4, lineHeight: 1.4 };

  const renderLLCForm = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6 }}>Company Name *</label>
        <input value={form.companyName || ''} onChange={e => set('companyName', e.target.value)} placeholder="e.g., Acme Technologies LLC" style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 15, boxSizing: 'border-box' }} />
        <p style={helpStyle}>The legal name of your LLC. Must include "LLC", "L.L.C.", or "Limited Liability Company" in most states. Check your state's naming requirements for restricted words.</p>
      </div>
      <div>
        <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6 }}>State of Formation *</label>
        <select value={form.state || ''} onChange={e => set('state', e.target.value)} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 15, background: 'white' }}>
          <option value="">Select state...</option>
          {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <p style={helpStyle}>The state where your LLC will be legally registered. Most small businesses form in their home state. Delaware and Wyoming are popular for asset protection and privacy. If you operate in a different state, you may need to register as a foreign LLC there too.</p>
      </div>
      <div>
        <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6 }}>Business Purpose</label>
        <input value={form.businessPurpose || ''} onChange={e => set('businessPurpose', e.target.value)} placeholder="Brief description of what the business does" style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 15, boxSizing: 'border-box' }} />
        <p style={helpStyle}>A brief description of your business activities. Many states accept a general purpose like "any lawful business activity." Being specific can help with banking and contracts but may limit future activities.</p>
      </div>
      <div>
        <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6 }}>Management Type</label>
        <div style={{ display: 'flex', gap: 12 }}>
          {['member-managed', 'manager-managed'].map(t => (
            <button key={t} onClick={() => set('managementType', t)} style={{ padding: '8px 20px', borderRadius: 8, border: `2px solid ${form.managementType === t ? '#6366f1' : '#e2e8f0'}`, background: form.managementType === t ? '#eef2ff' : 'white', color: form.managementType === t ? '#4338ca' : '#64748b', cursor: 'pointer', fontSize: 14, fontWeight: 500, textTransform: 'capitalize' }}>{t.replace('-', ' ')}</button>
          ))}
        </div>
        <p style={helpStyle}>
          <strong>Member Managed:</strong> All owners (members) participate in day-to-day decisions. Best for small businesses where all owners are actively involved.
          <br /><strong>Manager Managed:</strong> One or more designated managers run the business while other members are passive investors. Best when you have silent partners or outside investors.
        </p>
      </div>
      <button onClick={() => handleSubmit('llc')} disabled={!form.companyName || !form.state || loading} style={{ padding: '12px 28px', borderRadius: 8, border: 'none', background: '#6366f1', color: 'white', fontSize: 16, fontWeight: 600, cursor: 'pointer', opacity: !form.companyName || !form.state ? 0.5 : 1, marginTop: 8 }}>
        {loading ? 'Processing...' : 'Generate LLC Filing Package'}
      </button>
    </div>
  );

  const renderDBAForm = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6 }}>Business Name (DBA) *</label>
        <input value={form.businessName || ''} onChange={e => set('businessName', e.target.value)} placeholder="The name you want to operate under" style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 15, boxSizing: 'border-box' }} />
      </div>
      <div>
        <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6 }}>State *</label>
        <select value={form.state || ''} onChange={e => set('state', e.target.value)} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 15, background: 'white' }}>
          <option value="">Select state...</option>
          {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div>
        <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6 }}>County (if applicable)</label>
        <input value={form.county || ''} onChange={e => set('county', e.target.value)} placeholder="County name" style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 15, boxSizing: 'border-box' }} />
      </div>
      <div>
        <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6 }}>Owner Name *</label>
        <input value={form.ownerName || ''} onChange={e => set('ownerName', e.target.value)} placeholder="Legal name of business owner" style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 15, boxSizing: 'border-box' }} />
      </div>
      <button onClick={() => handleSubmit('dba')} disabled={!form.businessName || !form.state || !form.ownerName || loading} style={{ padding: '12px 28px', borderRadius: 8, border: 'none', background: '#0ea5e9', color: 'white', fontSize: 16, fontWeight: 600, cursor: 'pointer', opacity: !form.businessName || !form.state ? 0.5 : 1, marginTop: 8 }}>
        {loading ? 'Processing...' : 'Generate DBA Filing Package'}
      </button>
    </div>
  );

  const renderTrademarkForm = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6 }}>Trademark Name *</label>
        <input value={form.markName || ''} onChange={e => set('markName', e.target.value)} placeholder="The word, phrase, or symbol you want to trademark" style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 15, boxSizing: 'border-box' }} />
      </div>
      <div>
        <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6 }}>Mark Type</label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {['word mark', 'design mark', 'sound mark', 'combined'].map(t => (
            <button key={t} onClick={() => set('markType', t)} style={{ padding: '6px 14px', borderRadius: 20, border: `1px solid ${form.markType === t ? '#8b5cf6' : '#e2e8f0'}`, background: form.markType === t ? '#f5f3ff' : 'white', color: form.markType === t ? '#6d28d9' : '#64748b', cursor: 'pointer', fontSize: 13, textTransform: 'capitalize' }}>{t}</button>
          ))}
        </div>
      </div>
      <div>
        <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6 }}>Goods/Services Description *</label>
        <textarea value={form.goodsServices || ''} onChange={e => set('goodsServices', e.target.value)} placeholder="Describe the goods or services the mark will be used with (e.g., 'Online software platform for project management and team collaboration')" style={{ width: '100%', minHeight: 80, padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 15, resize: 'vertical', boxSizing: 'border-box' }} />
      </div>
      <div>
        <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6 }}>Owner/Applicant Name</label>
        <input value={form.ownerName || ''} onChange={e => set('ownerName', e.target.value)} placeholder="Individual or company name" style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 15, boxSizing: 'border-box' }} />
      </div>
      <button onClick={() => handleSubmit('trademark')} disabled={!form.markName || !form.goodsServices || loading} style={{ padding: '12px 28px', borderRadius: 8, border: 'none', background: '#8b5cf6', color: 'white', fontSize: 16, fontWeight: 600, cursor: 'pointer', opacity: !form.markName || !form.goodsServices ? 0.5 : 1, marginTop: 8 }}>
        {loading ? 'Processing...' : 'Analyze & Prepare Application'}
      </button>
    </div>
  );

  // Full-width when copilot is active
  if (showCopilot && currentRequest?.uuid) {
    return (
      <FilingCopilot
        uuid={currentRequest.uuid}
        filingGuide={currentRequest.results?.filingGuide}
        filingUrl={currentRequest.results?.requirements?.filingUrl}
        onBack={() => { setShowCopilot(false); navigate('/legal-services/' + currentRequest.uuid, { replace: true }); }}
      />
    );
  }

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '32px 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: '#1e293b', margin: 0 }}>Legal Services</h1>
          <p style={{ color: '#64748b', marginTop: 4 }}>File business formations, registrations, and trademark applications</p>
        </div>
        {activeService && <button onClick={reset} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#f1f5f9', color: '#475569', cursor: 'pointer', fontSize: 14 }}>Back to Services</button>}
      </div>

      {/* Service Selection */}
      {!activeService && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginBottom: 32 }}>
            {SERVICES.map(svc => (
              <div key={svc.id} onClick={() => { setActiveService(svc.id); setForm({}); setCurrentRequest(null); }} style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24, cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)'}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>{svc.icon}</div>
                <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 6, color: svc.color }}>{svc.title}</h3>
                <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.5 }}>{svc.description}</p>
                <span style={{ display: 'inline-block', marginTop: 12, color: svc.color, fontWeight: 500, fontSize: 14 }}>Get Started →</span>
              </div>
            ))}
          </div>
          {/* History */}
          {requests.length > 0 && (
            <div>
              <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>Previous Requests</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {requests.slice(0, 10).map(r => (
                  <div key={r.uuid} onClick={() => loadRequest(r)} style={{ padding: 14, borderRadius: 8, background: 'white', border: '1px solid #e2e8f0', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <span style={{ fontWeight: 500 }}>{r.title || r.type}</span>
                      <span style={{ fontSize: 12, color: '#9ca3af', marginLeft: 12 }}>{new Date(r.created_at).toLocaleDateString()}</span>
                    </div>
                    <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: r.status === 'completed' ? '#ecfdf5' : r.status === 'failed' ? '#fef2f2' : '#fef3c7', color: r.status === 'completed' ? '#059669' : r.status === 'failed' ? '#dc2626' : '#d97706' }}>{r.status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Active Service Form */}
      {activeService && !currentRequest && (
        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 32 }}>
          <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 20 }}>
            {SERVICES.find(s => s.id === activeService)?.title}
          </h2>
          {activeService === 'llc' && renderLLCForm()}
          {activeService === 'dba' && renderDBAForm()}
          {activeService === 'trademark' && renderTrademarkForm()}
        </div>
      )}

      {/* Processing */}
      {currentRequest?.status === 'processing' && (
        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 32 }}>
          <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 16 }}>Preparing Your Filing Package...</h2>
          <StepProgress steps={currentRequest.steps} />
        </div>
      )}

      {/* Results */}
      {currentRequest?.status === 'completed' && currentRequest.results && (
        <>
          {currentRequest.type === 'llc' && renderLLCResults(currentRequest.results)}
          {currentRequest.type === 'dba' && renderDBAResults(currentRequest.results)}
          {currentRequest.type === 'trademark' && renderTrademarkResults(currentRequest.results)}
        </>
      )}

      {/* Failed */}
      {currentRequest?.status === 'failed' && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12, padding: 20 }}>
          <p style={{ color: '#dc2626', fontWeight: 500 }}>Failed: {currentRequest.results?.error || 'Unknown error'}</p>
          <button onClick={reset} style={{ marginTop: 12, padding: '8px 20px', borderRadius: 8, border: 'none', background: '#dc2626', color: 'white', cursor: 'pointer' }}>Try Again</button>
        </div>
      )}

      <p style={{ textAlign: 'center', color: '#9ca3af', fontSize: 12, marginTop: 32 }}>
        AI-assisted legal document preparation. Consult an attorney before filing official documents.
      </p>
    </div>
  );
}
